/* Page Radar — orchestrateur. Composants split :
 *  - radar/EcosystemView    (vue legacy entité-scoped)  -- inline ici (relativement petit)
 *  - radar/WorldView        (scan mondial geo×secteur)
 *  - radar/SignalItem       (item de signal partagé)
 *  - radar/ImpactModal      (modal d'analyse d'impact AI + PDF)
 *  - radar/constants        (taxonomies + KPI)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Radio, RefreshCw, Loader2, AlertTriangle, Filter, Sparkles, Globe, Building2,
  Crown, Ghost, Shield, Activity, Briefcase, Landmark,
} from "lucide-react";
import { DefenseView } from "./radar/DefenseView";
import { SuccessionView } from "./radar/SuccessionView";
import { RegistryWatchView } from "./radar/RegistryWatchView";
import Trend from "./Trend";
import { ASYM_META, KPI } from "./radar/constants";
import { SignalItem } from "./radar/SignalItem";
import { WorldView } from "./radar/WorldView";
import { ImpactModal } from "./radar/ImpactModal";
import PhantomRadarView from "./radar/PhantomRadarView";
import MarketTransformationsPanel from "@/components/MarketTransformationsPanel";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import { usePersona } from "@/lib/persona";

export default function Radar() {
  const { isActionHidden } = usePersona();
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem("radar:tab") || "ecosystem"; } catch { return "ecosystem"; }
  });
  // Active l'onglet demandé via ?tab= (ex: redirection /app/trend -> ?tab=veille)
  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search).get("tab");
      if (qs) setActiveTab(qs);
    } catch { /* noop */ }
  }, []);

  // Ecosystem
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [byAsym, setByAsym] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  // World
  const [worldMatrix, setWorldMatrix] = useState(null);
  const [worldItems, setWorldItems] = useState([]);
  const [worldLoading, setWorldLoading] = useState(false);
  const [worldScanForm, setWorldScanForm] = useState({ continent: "", country: "", sector: "macro", level: "mondial" });
  const [worldScanning, setWorldScanning] = useState(false);
  const [worldFilter, setWorldFilter] = useState({ continent: "", sector: "" });
  // Impact modal
  const [impactSignal, setImpactSignal] = useState(null);
  const [impactData, setImpactData] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const selectTab = (t) => {
    setActiveTab(t);
    try { localStorage.setItem("radar:tab", t); } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, f, a] = await Promise.all([
        api.get("/workspace/radar/summary"),
        api.get("/workspace/radar/feed", { params: { unread_only: unreadOnly, scope: "ecosystem", limit: 100 } }),
        api.get("/workspace/radar/by-asymmetry", { params: { days: 30 } }),
      ]);
      setSummary(s.data);
      setItems(f.data?.items || []);
      setByAsym(a.data);
    } catch (e) {
      notify.error(fmtError(e, "Chargement radar impossible."));
    } finally { setLoading(false); }
  };

  const loadWorld = async () => {
    setWorldLoading(true);
    try {
      const params = { scope: "world", limit: 100 };
      if (worldFilter.continent) params.region = worldFilter.continent;
      if (worldFilter.sector) params.category = worldFilter.sector;
      const [m, f] = await Promise.all([
        api.get("/workspace/radar/world/matrix"),
        api.get("/workspace/radar/feed", { params }),
      ]);
      setWorldMatrix(m.data);
      setWorldItems(f.data?.items || []);
    } catch (e) {
      notify.error(fmtError(e, "Chargement scan mondial impossible."));
    } finally { setWorldLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unreadOnly]);
  useEffect(() => {
    if (activeTab === "world") loadWorld();
    /* eslint-disable-next-line */
  }, [activeTab, worldFilter.continent, worldFilter.sector]);

  const ack = async (sid) => {
    try {
      await api.post(`/workspace/radar/${sid}/ack`);
      setItems((arr) => arr.map((it) => it.id === sid ? { ...it, read: true } : it));
      setWorldItems((arr) => arr.map((it) => it.id === sid ? { ...it, read: true } : it));
      setSummary((s) => s ? { ...s, unread: Math.max(0, (s.unread || 0) - 1) } : s);
    } catch (e) { notify.error(fmtError(e, "Marquage impossible.")); }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/workspace/radar/run");
      notify.success(`Scan terminé : ${r.data?.new_signals || 0} nouveaux signaux.`);
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Scan impossible."));
    } finally { setScanning(false); }
  };

  const reclassify = async () => {
    setReclassifying(true);
    try {
      const r = await api.post("/workspace/radar/reclassify", null, { params: { max_items: 100 } });
      const { tagged = 0, processed = 0, remaining_unclassified = 0 } = r.data || {};
      notify.success(`Reclassification fractale : ${tagged} signal(aux) étiqueté(s) sur ${processed} traités. Restant : ${remaining_unclassified}.`);
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Reclassification impossible."));
    } finally { setReclassifying(false); }
  };

  const runWorldScan = async () => {
    if (!worldScanForm.sector) { notify.error("Choisis au moins un secteur."); return; }
    setWorldScanning(true);
    try {
      const r = await api.post("/workspace/radar/world/scan", worldScanForm);
      notify.success(`Scan mondial : ${r.data?.created || 0} nouveau(x) signal(aux) (${r.data?.sources_found || 0} sources analysées).`);
      await loadWorld();
    } catch (e) {
      notify.error(fmtError(e, "Scan mondial impossible."));
    } finally { setWorldScanning(false); }
  };

  const openImpact = async (signal) => {
    setImpactSignal(signal);
    if (signal.impact) { setImpactData(signal.impact); return; }
    setImpactData(null);
    setImpactLoading(true);
    try {
      const r = await api.post(`/workspace/radar/signals/${signal.id}/impact`);
      setImpactData(r.data);
      const patch = (arr) => arr.map((it) => it.id === signal.id ? { ...it, impact: r.data } : it);
      setItems(patch);
      setWorldItems(patch);
    } catch (e) {
      notify.error(fmtError(e, "Analyse d'impact impossible."));
      setImpactSignal(null);
    } finally { setImpactLoading(false); }
  };

  const recomputeImpact = async () => {
    if (!impactSignal) return;
    setImpactData(null);
    setImpactLoading(true);
    try {
      const r = await api.post(`/workspace/radar/signals/${impactSignal.id}/impact`);
      setImpactData(r.data);
      const patch = (arr) => arr.map((it) => it.id === impactSignal.id ? { ...it, impact: r.data } : it);
      setItems(patch);
      setWorldItems(patch);
    } catch (e) {
      notify.error(fmtError(e, "Recalcul impossible."));
    } finally { setImpactLoading(false); }
  };

  const exportImpactPdf = async () => {
    if (!impactSignal || !impactData) return;
    try {
      const r = await api.post(`/workspace/radar/signals/${impactSignal.id}/impact/pdf`, null, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `brief-impact-${impactSignal.id.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notify.success("Brief PDF généré.");
    } catch (e) {
      notify.error(fmtError(e, "Export PDF impossible."));
    }
  };

  return (
    <div className="space-y-5" data-testid="radar-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#1D9E75]">
            <Radio className="w-3 h-3" /> Veille passive
          </div>
          <h1 className="noam-display text-2xl font-bold tracking-tight mt-1">Noam Radar</h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-3xl leading-relaxed">
            Veille personnalisée sur vos entités (<strong className="text-zinc-200">Mon écosystème</strong>) et
            balayage géographie × secteur du monde entier (<strong className="text-zinc-200">Scan mondial</strong>).
            Pour chaque signal, demandez à Noam d'évaluer son impact sur votre organisation.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ModuleHelpButton moduleKey="radar" />
          {activeTab === "ecosystem" && (
            <>
              <button type="button" onClick={reclassify} disabled={reclassifying} className="btn-ghost text-xs disabled:opacity-50" data-testid="radar-reclassify-btn" title="Reclasse les anciens signaux selon la Doctrine fractale (amont/aval/soutien)">
                {reclassifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {reclassifying ? "Classement…" : "Reclasser l'historique"}
              </button>
              <button type="button" onClick={scan} disabled={scanning || !summary?.active} className="btn-primary text-xs disabled:opacity-50" data-testid="radar-scan-btn">
                {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {scanning ? "Scan…" : "Scanner mes entités"}
              </button>
            </>
          )}
        </div>
      </div>

      {summary && !summary.active && (
        <div className="noam-card p-4 flex items-start gap-2 border-[#EF9F27]/30 bg-[#EF9F27]/5">
          <AlertTriangle className="w-4 h-4 text-[#EF9F27] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-300">
            <strong className="text-[#EF9F27]">Radar inactif.</strong> Ajoute <code className="px-1 bg-white/10 rounded">TAVILY_API_KEY</code> dans <code className="px-1 bg-white/10 rounded">backend/.env</code> puis redémarre le backend pour activer la veille passive.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10" data-testid="radar-tabs">
        <TabButton active={activeTab === "ecosystem"} onClick={() => selectTab("ecosystem")} testId="radar-tab-ecosystem">
          <Building2 className="w-3.5 h-3.5" /> Mon écosystème
        </TabButton>
        <TabButton active={activeTab === "world"} onClick={() => selectTab("world")} testId="radar-tab-world">
          <Globe className="w-3.5 h-3.5" /> Scan mondial
        </TabButton>
        <TabButton active={activeTab === "defense"} onClick={() => selectTab("defense")} testId="radar-tab-defense">
          <Shield className="w-3.5 h-3.5" /> Défense mondiale
        </TabButton>
        <TabButton active={activeTab === "veille"} onClick={() => selectTab("veille")} testId="radar-tab-veille">
          <Activity className="w-3.5 h-3.5" /> Veille (flux)
        </TabButton>
        <TabButton active={activeTab === "succession"} onClick={() => selectTab("succession")} testId="radar-tab-succession">
          <Briefcase className="w-3.5 h-3.5" /> Succession PME
        </TabButton>
        <TabButton active={activeTab === "registre"} onClick={() => selectTab("registre")} testId="radar-tab-registre">
          <Landmark className="w-3.5 h-3.5" /> Registre
        </TabButton>
        <TabButton active={activeTab === "phantoms"} onClick={() => selectTab("phantoms")} testId="radar-tab-phantoms">
          <Ghost className="w-3.5 h-3.5" /> Fantômes
        </TabButton>
        <TabButton active={activeTab === "transformations"} onClick={() => selectTab("transformations")} testId="radar-tab-transformations">
          <Radio className="w-3.5 h-3.5" /> Transformations marché
        </TabButton>
      </div>

      {activeTab === "transformations" ? (
        <MarketTransformationsPanel />
      ) : activeTab === "defense" ? (
        <DefenseView />
      ) : activeTab === "succession" ? (
        <SuccessionView />
      ) : activeTab === "registre" ? (
        <RegistryWatchView />
      ) : activeTab === "veille" ? (
        <Trend embedded />
      ) : activeTab === "ecosystem" ? (
        <EcosystemView summary={summary} byAsym={byAsym} items={items} loading={loading} unreadOnly={unreadOnly} setUnreadOnly={setUnreadOnly} ack={ack} openImpact={openImpact} />
      ) : activeTab === "world" ? (
        <WorldView
          matrix={worldMatrix}
          items={worldItems}
          loading={worldLoading}
          scanning={worldScanning}
          form={worldScanForm}
          setForm={setWorldScanForm}
          filter={worldFilter}
          setFilter={setWorldFilter}
          runScan={runWorldScan}
          ack={ack}
          openImpact={openImpact}
          active={summary?.active}
        />
      ) : (
        <PhantomRadarView />
      )}

      {impactSignal && (
        <ImpactModal
          signal={impactSignal}
          data={impactData}
          loading={impactLoading}
          onClose={() => { setImpactSignal(null); setImpactData(null); }}
          onRecompute={recomputeImpact}
          onExportPdf={isActionHidden("export_pdf") ? null : exportImpactPdf}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`relative inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
        active ? "text-[#378ADD]" : "text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {children}
      {active && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-[#378ADD]" />}
    </button>
  );
}

function EcosystemView({ summary, byAsym, items, loading, unreadOnly, setUnreadOnly, ack, openImpact }) {
  return (
    <>
      {/* V23.15.35 — Doctrine 3Q · signal radar critique du moment */}
      <ThreeQPageHeader endpoint="/workspace/radar/top-priority" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Signaux non lus" value={summary?.unread || 0} accent="#EF9F27" />
        <KPI label="Total détectés" value={summary?.total || 0} accent="#378ADD" />
        <KPI label="Entités scannées" value={summary?.last_run?.entities_scanned || 0} accent="#1D9E75" />
      </div>

      {byAsym?.groups?.length > 0 && (
        <div className="noam-card p-4 space-y-3 overflow-hidden" data-testid="radar-by-asymmetry">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Crown className="w-3 h-3 text-[#EF9F27]" />
              <div className="noam-overline">Doctrine vivante — Signaux par asymétrie touchée ({byAsym.days}j)</div>
            </div>
            <span className="text-[10px] text-zinc-500">
              {byAsym.total_classified_signals} signal{byAsym.total_classified_signals > 1 ? "s" : ""} classifié{byAsym.total_classified_signals > 1 ? "s" : ""} · trié par captivité décroissante
            </span>
          </div>
          <p className="text-[11px] text-zinc-400 italic">
            Les types les plus difficiles à remplacer apparaissent en premier — agis là où ton avantage est le plus défendable.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {byAsym.groups.map((g) => {
              const meta = ASYM_META[g.type] || { label: g.type, icon: Radio, color: "#71717a" };
              const Icon = meta.icon;
              return (
                <div key={g.type} className="border rounded-md p-3 bg-white/[0.02] min-w-0 overflow-hidden" style={{ borderColor: `${meta.color}30` }} data-testid={`radar-asym-${g.type}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 min-w-0" style={{ color: meta.color }}>
                      <Icon className="w-3 h-3 flex-shrink-0" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider truncate">{meta.label}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {g.unread > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#EF9F27]/20 text-[#EF9F27] font-bold whitespace-nowrap">
                          {g.unread} non lu{g.unread > 1 ? "s" : ""}
                        </span>
                      )}
                      <span className="text-[10px] text-zinc-500">×{g.count}</span>
                    </div>
                  </div>
                  <div className="text-[9px] text-zinc-500 mb-1.5">
                    Difficulté de remplacement : {Math.round(g.replacement_difficulty * 100)}/100
                  </div>
                  <ul className="space-y-1 min-w-0">
                    {g.recent.map((r) => (
                      <li key={r.id} className="text-[10px] min-w-0">
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className={`hover:underline flex items-center gap-1 min-w-0 ${r.read ? "text-zinc-500" : "text-zinc-200"}`} title={r.title}>
                          {!r.read && <span className="w-1 h-1 rounded-full bg-[#EF9F27] flex-shrink-0" />}
                          <span className="truncate min-w-0 flex-1">{r.title}</span>
                        </a>
                        <div className="text-zinc-600 text-[9px] truncate">{r.entity_name}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Filter className="w-3 h-3 text-zinc-500" />
        <button onClick={() => setUnreadOnly(false)} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${!unreadOnly ? "border-[#378ADD] text-[#378ADD] bg-[#378ADD]/10" : "border-white/10 text-zinc-500"}`} data-testid="radar-filter-all">Tous</button>
        <button onClick={() => setUnreadOnly(true)} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${unreadOnly ? "border-[#EF9F27] text-[#EF9F27] bg-[#EF9F27]/10" : "border-white/10 text-zinc-500"}`} data-testid="radar-filter-unread">Non lus</button>
      </div>

      {loading ? (
        <div className="text-[11px] text-zinc-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="noam-card p-12 text-center" data-testid="radar-empty">
          <Radio className="w-10 h-10 text-[#1D9E75] mx-auto opacity-50" />
          <div className="noam-display text-base mt-3">
            {unreadOnly ? "Tout est lu" : "Aucun signal pour le moment"}
          </div>
          <p className="text-zinc-400 text-xs mt-2 max-w-sm mx-auto">
            {unreadOnly
              ? "Bravo. Tu as parcouru tous les signaux radar."
              : "Le radar s'active à chaque scan hebdomadaire. Lance un scan manuel pour voir les signaux dès maintenant."}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="radar-feed">
          {items.map((s) => (
            <SignalItem key={s.id} s={s} ack={ack} openImpact={openImpact} />
          ))}
        </ul>
      )}
    </>
  );
}

